import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, Waypoints, X } from "lucide-react";
import { allConceptProgress, allFigureProgress, LEVEL_LABEL } from "../../engine";
import type { ConceptProgress, FigureProgress } from "../../engine";
import { useGame } from "../../state/GameProvider";

/**
 * 知識圖譜 — the book by idea and by person, rather than by page.
 *
 * Each node's fill is that concept's mastery, so the map doubles as the
 * growth visualisation: the shape of what you understand, and the holes in it.
 */
export function MapView() {
  const { state, actions } = useGame();
  const [tab, setTab] = useState<"concept" | "figure">("concept");
  const [openConcept, setOpenConcept] = useState<ConceptProgress | null>(null);
  const [openFigure, setOpenFigure] = useState<FigureProgress | null>(null);

  const concepts = allConceptProgress(state.progress).sort((a, b) => b.percent - a.percent);
  const figures = allFigureProgress(state.progress).sort((a, b) => b.percent - a.percent);

  return (
    <div className="view map">
      <header className="view-head">
        <h2>知識圖譜</h2>
        <p>亮起來的是你已經讀通的部分。</p>
      </header>

      <div className="tabs">
        <button className={tab === "concept" ? "on" : ""} onClick={() => setTab("concept")}>
          <Waypoints size={14} /> 核心概念
        </button>
        <button className={tab === "figure" ? "on" : ""} onClick={() => setTab("figure")}>
          <Users size={14} /> 人物
        </button>
      </div>

      {tab === "concept" ? (
        <div className="node-grid">
          {concepts.map((entry, index) => (
            <motion.button
              key={entry.concept.id}
              className={`node lv${entry.level}`}
              onClick={() => setOpenConcept(entry)}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25, delay: index * 0.02 }}
              style={{ "--fill": `${entry.percent}%` } as React.CSSProperties}
            >
              <span className="node-glyph">{entry.concept.glyph}</span>
              <span className="node-label">{entry.concept.label}</span>
              <span className="node-pct">{entry.percent}%</span>
            </motion.button>
          ))}
        </div>
      ) : (
        <div className="figure-list">
          {figures.map((entry, index) => (
            <motion.button
              key={entry.figure.id}
              className="figure-row"
              onClick={() => setOpenFigure(entry)}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25, delay: index * 0.02 }}
            >
              <span className="fig-name">{entry.figure.name}</span>
              <span className="fig-trait">{entry.figure.trait}</span>
              <span className="fig-meta">
                {entry.mastered}/{entry.total}
              </span>
              <i className="fig-fill" style={{ width: `${entry.percent}%` }} />
            </motion.button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {openConcept && (
          <motion.div
            className="sheet-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenConcept(null)}
          >
            <motion.section
              className="sheet"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              onClick={(event) => event.stopPropagation()}
            >
              <button className="sheet-close" onClick={() => setOpenConcept(null)} aria-label="關閉">
                <X size={18} />
              </button>
              <h3>
                {openConcept.concept.label}
                <small>
                  {LEVEL_LABEL[openConcept.level]} · {openConcept.percent}%
                </small>
              </h3>
              <p className="sheet-q">{openConcept.concept.question}</p>
              <p className="sheet-gist">{openConcept.concept.gist}</p>
              <div className="sheet-pitfall">
                <b>最常見的誤解</b>
                {openConcept.concept.pitfall}
              </div>
              <div className="sheet-stats">
                <span>相關章 {openConcept.total}</span>
                <span>已通 {openConcept.mastered}</span>
              </div>
              <button
                className="sheet-cta"
                onClick={() => {
                  const first = openConcept.concept.keyPassages[0];
                  setOpenConcept(null);
                  actions.start({ id: first });
                }}
              >
                從關鍵章讀起
              </button>
            </motion.section>
          </motion.div>
        )}

        {openFigure && (
          <motion.div
            className="sheet-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenFigure(null)}
          >
            <motion.section
              className="sheet"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              onClick={(event) => event.stopPropagation()}
            >
              <button className="sheet-close" onClick={() => setOpenFigure(null)} aria-label="關閉">
                <X size={18} />
              </button>
              <h3>
                {openFigure.figure.name}
                <small>{openFigure.figure.aka}</small>
              </h3>
              <p className="sheet-gist">{openFigure.figure.trait}</p>
              <div className="sheet-pitfall">
                <b>讀他的章要注意</b>
                {openFigure.figure.note}
              </div>
              <div className="sheet-stats">
                <span>相關章 {openFigure.total}</span>
                <span>已通 {openFigure.mastered}</span>
              </div>
              <button
                className="sheet-cta"
                onClick={() => {
                  const first = openFigure.figure.keyPassages[0];
                  setOpenFigure(null);
                  actions.start({ id: first });
                }}
              >
                從關鍵章讀起
              </button>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
